import { FC } from 'react';
import { css } from '@emotion/react';
import { useHistory } from 'react-router-dom';
import { colors, spacing, fontStyles } from '../../../styles';
import { ToolRendererProps } from './types';

// Custom hook for safe router usage (works in test environments without router)
const useSafeHistory = () => {
  try {
    return useHistory();
  } catch {
    return null;
  }
};

// Progress bar component for visual representation
const ProgressBar: FC<{ value: number; maxValue: number; filled?: string; empty?: string }> = ({
  value,
  maxValue,
  filled = '█',
  empty = '░',
}) => {
  const barLength = 14;
  const filledLength = Math.round((value / maxValue) * barLength);
  const emptyLength = barLength - filledLength;
  return (
    <span>
      {filled.repeat(Math.max(0, filledLength))}
      {empty.repeat(Math.max(0, emptyLength))}
    </span>
  );
};

// Correlation bar using different characters
const CorrelationBar: FC<{ value: number }> = ({ value }) => {
  const barLength = 10;
  const filledLength = Math.round(value * barLength);
  const emptyLength = barLength - filledLength;
  return (
    <span>
      {'▓'.repeat(Math.max(0, filledLength))}
      {'░'.repeat(Math.max(0, emptyLength))}
    </span>
  );
};

// Format return percentage with sign
// isDecimal=true means value is in decimal form (0.15 = 15%), false means already a percentage
const formatReturn = (value: number | undefined, isDecimal = true): string => {
  if (value === undefined || value === null || isNaN(value)) return 'N/A';
  const percent = isDecimal ? value * 100 : value;
  const sign = percent >= 0 ? '+' : '';
  return `${sign}${percent.toFixed(1)}%`;
};

// Format percentage
const formatPercent = (value: number | undefined): string => {
  if (value === undefined || value === null || isNaN(value)) return 'N/A';
  return `${(value * 100).toFixed(1)}%`;
};

// Get return color
const getReturnColor = (value: number | undefined): string | undefined => {
  if (value === undefined || value === null || isNaN(value)) return undefined;
  if (value > 0) return colors.leaf;
  if (value < 0) return colors.berry;
  return undefined;
};

// Extract return value from either {return_percent: number} or plain number
const extractReturnValue = (val: unknown): number | undefined => {
  if (val === undefined || val === null) return undefined;
  if (typeof val === 'number') return val;
  if (typeof val === 'object' && val !== null && 'return_percent' in val) {
    return (val as { return_percent: number }).return_percent;
  }
  return undefined;
};

interface PortfolioSummary {
  symbols?: string[];
  allocations?: number[];
  analysis_period?: string;
  total_allocation?: number;
}

interface PortfolioReturns {
  [period: string]: {
    return_percent?: number;
    top_contributor?: string;
    top_detractor?: string;
  };
}

interface WeightedReturns {
  [period: string]: number | PortfolioReturns | string | undefined;
  portfolio_returns?: PortfolioReturns;
  error?: string;
}

interface CorrelationAnalysis {
  period_analyzed?: string;
  correlation_matrix?: { [symbol: string]: { [symbol: string]: number } };
  matrix?: number[][] | { [symbol: string]: { [symbol: string]: number } };
  annualized_return?: { [symbol: string]: number };
  analysis?: string;
  insights?: string;
  error?: string;
}

interface RiskAnalysis {
  period_analyzed?: string;
  max_drawdown?: number;
  avg_drawdown?: number;
  // Flat fields (actual API response)
  sharpe_ratio?: number;
  var_95?: number;
  cvar_95?: number;
  // Nested fields (legacy/alternate format)
  risk_metrics?: {
    sharpe_ratio?: number;
    var_95?: number;
    cvar_95?: number;
  };
  error?: string;
}

interface PortfolioAnalysisOutput {
  portfolio_summary?: PortfolioSummary;
  weighted_returns?: WeightedReturns;
  correlation_analysis?: CorrelationAnalysis;
  risk_analysis?: RiskAnalysis;
  timestamp?: string;
  error?: string;
}

// Extract correlation pairs from matrix (excluding self-correlations)
const getCorrelationPairs = (
  matrix: { [symbol: string]: { [symbol: string]: number } } | undefined,
): { pair: string; correlation: number }[] => {
  if (!matrix) return [];
  const pairs: { pair: string; correlation: number }[] = [];
  const symbols = Object.keys(matrix);

  symbols.forEach((symbol1) => {
    symbols.forEach((symbol2) => {
      if (symbol1 < symbol2) {
        const correlation = matrix[symbol1]?.[symbol2];
        if (correlation !== undefined && !isNaN(correlation)) {
          pairs.push({ pair: `${symbol1}-${symbol2}`, correlation });
        }
      }
    });
  });

  return pairs.sort((a, b) => b.correlation - a.correlation);
};

// Portfolio Renderer - Display comprehensive portfolio analysis
export const PortfolioRenderer: FC<ToolRendererProps> = ({ output, isLoading }) => {
  const history = useSafeHistory();

  const handleSymbolClick = (symbol: string) => {
    // Validate symbol format (alphanumeric + common separators, reasonable length)
    const isValidSymbol = /^[A-Z0-9\-\.]{1,10}$/i.test(symbol);
    if (symbol && symbol !== 'N/A' && isValidSymbol && history) {
      history.push(`/symbol/${encodeURIComponent(symbol)}`);
    }
  };

  if (isLoading) {
    return (
      <div
        css={css`
          ${fontStyles.cat}
          color: ${colors.silver};
        `}
      >
        Analyzing portfolio...
      </div>
    );
  }

  // Cast output to expected structure
  const data = output as PortfolioAnalysisOutput;

  // Check for error
  if (data.error) {
    return (
      <div
        css={css`
          ${fontStyles.cat}
          color: ${colors.berry};
        `}
      >
        Error: {data.error}
      </div>
    );
  }

  const portfolioSummary = data.portfolio_summary;
  // Old format: {portfolio_returns: {1d: {return_percent: 0.0023}, ...}} (decimal)
  // New MCP format: {1d: 0.23, 1w: 1.5, ...} (already percentage)
  const hasOldFormat = !!data.weighted_returns?.portfolio_returns;
  const weightedReturns = data.weighted_returns?.portfolio_returns || data.weighted_returns;
  const returnsAreDecimal = hasOldFormat;
  const riskAnalysis = data.risk_analysis;
  // BUG 2 fix: actual key may be "matrix" (2D array) instead of "correlation_matrix" (dict-of-dicts)
  const rawCorrelationMatrix = data.correlation_analysis?.correlation_matrix || data.correlation_analysis?.matrix;

  // Convert 2D array matrix to dict-of-dicts if needed
  let correlationMatrix: { [symbol: string]: { [symbol: string]: number } } | undefined;
  if (Array.isArray(rawCorrelationMatrix)) {
    const syms = data.portfolio_summary?.symbols || [];
    const dict: { [symbol: string]: { [symbol: string]: number } } = {};
    rawCorrelationMatrix.forEach((row: number[], i: number) => {
      if (syms[i]) {
        dict[syms[i]] = {};
        row.forEach((val: number, j: number) => {
          if (syms[j]) {
            dict[syms[i]][syms[j]] = val;
          }
        });
      }
    });
    correlationMatrix = dict;
  } else {
    correlationMatrix = rawCorrelationMatrix as { [symbol: string]: { [symbol: string]: number } } | undefined;
  }

  // If no valid data, show fallback
  if (!portfolioSummary?.symbols?.length) {
    return (
      <div
        css={css`
          ${fontStyles.cat}
          color: ${colors.silver};
        `}
      >
        Portfolio analysis completed
      </div>
    );
  }

  const symbols = portfolioSummary.symbols || [];
  const allocations = portfolioSummary.allocations || [];

  // Time periods to display
  const periods = ['1d', '1w', '1m', '3m', '6m', 'ytd', '1y', '5y'];
  const periodLabels: { [key: string]: string } = {
    '1d': '1D',
    '1w': '1W',
    '1m': '1M',
    '3m': '3M',
    '6m': '6M',
    ytd: 'YTD',
    '1y': '1Y',
    '5y': '5Y',
  };

  // Find max return for scaling progress bars
  const allReturns = periods
    .map((p) => Math.abs(extractReturnValue(weightedReturns?.[p]) || 0))
    .filter((v) => !isNaN(v));
  const maxReturn = Math.max(...allReturns, 0.01); // Use 0.01 (1%) as minimum to avoid division issues

  // Get correlation pairs
  const correlationPairs = getCorrelationPairs(correlationMatrix).slice(0, 3);

  const containerStyles = css`
    ${fontStyles.cat}
    color: ${colors.liquorice};
    border: 1px solid ${colors.cloud};
    border-radius: 8px;
    overflow: hidden;

    html[data-theme='dark'] & {
      color: ${colors.sugar};
      border-color: ${colors.tungsten};
    }
  `;

  const gridStyles = css`
    display: grid;
    grid-template-columns: 1fr 1fr;

    @media (max-width: 500px) {
      grid-template-columns: 1fr;
    }
  `;

  const sectionStyles = css`
    padding: ${spacing.centi} ${spacing.deci};

    /* Top row: Holdings and Returns - add bottom border */
    &:nth-of-type(1),
    &:nth-of-type(2) {
      border-bottom: 1px solid ${colors.cloud};
    }

    /* Left column: Holdings and Risk - add right border */
    &:nth-of-type(odd) {
      border-right: 1px solid ${colors.cloud};

      @media (max-width: 500px) {
        border-right: none;
      }
    }

    /* Mobile: all sections except last get bottom border */
    @media (max-width: 500px) {
      &:not(:last-child) {
        border-bottom: 1px solid ${colors.cloud};
      }
    }

    html[data-theme='dark'] & {
      border-color: ${colors.tungsten};
    }
  `;

  const sectionTitleStyles = css`
    ${fontStyles.flea}
    color: ${colors.silver};
    margin-bottom: ${spacing.centi};
    text-transform: uppercase;
    letter-spacing: 0.5px;

    html[data-theme='dark'] & {
      color: ${colors.tungsten};
    }
  `;

  const rowStyles = css`
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: ${spacing.milli};
    font-family: monospace;
    font-size: 12px;
    line-height: 1.4;

    &:last-child {
      margin-bottom: 0;
    }
  `;

  const symbolButtonStyles = css`
    background: none;
    border: none;
    padding: 0;
    font-weight: 500;
    min-width: 50px;
    color: ${colors.petrolBlue};
    cursor: pointer;
    text-decoration: none;
    text-align: left;
    font-size: inherit;
    font-family: inherit;

    &:hover,
    &:focus {
      text-decoration: underline;
    }

    &:focus {
      outline: 2px solid ${colors.petrolBlue};
      outline-offset: 2px;
    }

    html[data-theme='dark'] & {
      color: ${colors.skyBlue};

      &:focus {
        outline-color: ${colors.skyBlue};
      }
    }
  `;

  const barStyles = css`
    color: ${colors.silver};
    margin: 0 ${spacing.milli};
    letter-spacing: -1px;

    html[data-theme='dark'] & {
      color: ${colors.tungsten};
    }
  `;

  const valueStyles = css`
    text-align: right;
    min-width: 55px;
  `;

  const periodStyles = css`
    min-width: 30px;
    color: ${colors.silver};

    html[data-theme='dark'] & {
      color: ${colors.tungsten};
    }
  `;

  const riskLabelStyles = css`
    color: ${colors.liquorice};

    html[data-theme='dark'] & {
      color: ${colors.sugar};
    }
  `;

  const correlationBarStyles = css`
    color: ${colors.petrolBlue};
    margin: 0 ${spacing.milli};
    letter-spacing: -1px;

    html[data-theme='dark'] & {
      color: ${colors.skyBlue};
    }
  `;

  // Non-clickable pair label for correlation section (standard text color)
  const pairLabelStyles = css`
    color: ${colors.liquorice};
    font-weight: 500;
    min-width: 80px;

    html[data-theme='dark'] & {
      color: ${colors.sugar};
    }
  `;

  return (
    <div css={containerStyles}>
      {/* Grid Layout */}
      <div css={gridStyles}>
        {/* Holdings Section */}
        <div css={sectionStyles}>
          <div css={sectionTitleStyles}>Holdings</div>
          {symbols.map((symbol, index) => {
            const allocation = allocations[index] || 0;
            return (
              <div key={symbol} css={rowStyles}>
                <button
                  type="button"
                  css={symbolButtonStyles}
                  onClick={() => handleSymbolClick(symbol)}
                  aria-label={`View details for ${symbol}`}
                >
                  {symbol}
                </button>
                <span css={barStyles}>
                  <ProgressBar value={allocation} maxValue={1} />
                </span>
                <span css={valueStyles}>{formatPercent(allocation)}</span>
              </div>
            );
          })}
        </div>

        {/* Returns Section */}
        <div css={sectionStyles}>
          <div css={sectionTitleStyles}>Returns</div>
          {periods.map((period) => {
            const returnPercent = extractReturnValue(weightedReturns?.[period]);
            const returnColor = getReturnColor(returnPercent);

            return (
              <div key={period} css={rowStyles}>
                <span css={periodStyles}>{periodLabels[period]}</span>
                <span css={barStyles}>
                  <ProgressBar value={Math.abs(returnPercent || 0)} maxValue={maxReturn} />
                </span>
                <span
                  css={css`
                    ${valueStyles}
                    color: ${returnColor};
                  `}
                >
                  {formatReturn(returnPercent, returnsAreDecimal)}
                </span>
              </div>
            );
          })}
        </div>

        {/* Risk Section */}
        <div css={sectionStyles}>
          <div css={sectionTitleStyles}>Risk</div>
          <div css={rowStyles}>
            <span css={riskLabelStyles}>Max Drawdown</span>
            <span
              css={css`
                ${valueStyles}
                color: ${colors.berry};
              `}
            >
              {riskAnalysis?.max_drawdown !== undefined
                ? `${(returnsAreDecimal ? riskAnalysis.max_drawdown * 100 : riskAnalysis.max_drawdown).toFixed(1)}%`
                : 'N/A'}
            </span>
          </div>
          <div css={rowStyles}>
            <span css={riskLabelStyles}>Avg Drawdown</span>
            <span
              css={css`
                ${valueStyles}
                color: ${colors.berry};
              `}
            >
              {riskAnalysis?.avg_drawdown !== undefined
                ? `${(returnsAreDecimal ? riskAnalysis.avg_drawdown * 100 : riskAnalysis.avg_drawdown).toFixed(1)}%`
                : 'N/A'}
            </span>
          </div>
          <div css={rowStyles}>
            <span css={riskLabelStyles}>Sharpe Ratio</span>
            <span css={valueStyles}>
              {(riskAnalysis?.sharpe_ratio ?? riskAnalysis?.risk_metrics?.sharpe_ratio) !== undefined
                ? (riskAnalysis?.sharpe_ratio ?? riskAnalysis?.risk_metrics?.sharpe_ratio)!.toFixed(2)
                : 'N/A'}
            </span>
          </div>
        </div>

        {/* Correlation Section */}
        <div css={sectionStyles}>
          <div css={sectionTitleStyles}>Correlation</div>
          {correlationPairs.length > 0 ? (
            correlationPairs.map(({ pair, correlation }) => (
              <div key={pair} css={rowStyles}>
                <span css={pairLabelStyles}>{pair}</span>
                <span css={correlationBarStyles}>
                  <CorrelationBar value={correlation} />
                </span>
                <span css={valueStyles}>{correlation.toFixed(2)}</span>
              </div>
            ))
          ) : (
            <div
              css={css`
                color: ${colors.silver};
              `}
            >
              No correlation data
            </div>
          )}
        </div>
      </div>
    </div>
  );
};
